/**
 * 서랍을 여는 목록들의 **열이 빠지는 차례** — 한 곳에 모은 표(eric 2026-09-28 「목록의 일부 열을 공간이 모자라면
 * 숨겨야지」). 판정은 공용 표(`DataTable` · `atoms/columnFit.ts`)가 하고, 여기는 «무엇을 먼저»만 정한다.
 *
 * 숫자가 낮을수록 먼저 빠진다. **여기 없는 열은 늘 보인다.**
 *
 * 고른 기준
 *  ⓐ 줄을 식별하는 첫 열 · 상태/등급처럼 판단에 쓰는 열은 넣지 않는다(끝까지 남는다)
 *  ⓑ 서랍에 이미 같은 값이 보이는 열부터
 *  ⓒ 시각 · 보조 식별자 · 분류처럼 부가적인 것을 먼저
 *
 * 🔴 **여기 넣은 열의 값은 그 화면의 서랍에서 볼 수 있어야 한다.** 목록에서 빠지면 갈 곳이 서랍뿐이다.
 *    시험이 화면마다 그 짝을 본다(`drawerNoRepeat.test.tsx` · `alarmDrawer.test.tsx`). 열을 넣으면 서랍도 확인하라.
 * ⚠ 표가 이 우선순위를 쓰는 것은 **서랍을 여는 목록(통합 콘솔)**에서다. 같은 목록 컴포넌트를 장비 관점 콘솔 ·
 *   태블릿이 써도 그쪽 상자는 넓어서(서랍이 없어서) 실제로 빠지는 일은 거의 없다 — 빠지더라도 같은 차례다.
 * ⚠ 랩웨어 목록은 넣지 않았다 — 열이 이름 · 타입 둘뿐이라(eric 2026-09-30) 서랍이 열려도 넘치지 않는다. 둘 다 끝까지 남는 열이다.
 */
export const LIST_COLUMN_PRIORITY = {
  /**
   * 발생 시각 · 심각도 · 알람 · 상태는 끝까지 남는다. 빠지는 것은 모듈 하나(서랍 「요약」 카드가 적는다).
   * 🔴 2026-09-30 열 차례가 「발생 시각 · 모듈 · 심각도 · 알람 · 상태」로 바뀌었다(eric). 발생 시각(`occurredAt`)이 첫 열이라
   *    `priority` 를 줘도 안 빠진다 — 그래서 여기 없다. 「분류」 열은 목록에서 뺐다(서랍 · 펼친 행에 값이 있다)
   */
  alarms: { module: 1 },
  /**
   * 작업 ID · 상태는 끝까지 남는다. 종료 → 생성 → 시작 → 시료 수 → 작업자 → 레시피(빠지는 차례 · 열 차례와 별개)
   * 🔴 2026-09-30 열이 이력과 같아졌다(eric 「이력 컬럼에서 소요 시간 빼고 동일하게」) — 「진행」이 빠지고 「종료」가 들어왔다.
   *    진행 중 목록이라 종료는 대개 `—` 여서 **가장 먼저** 빠진다. 그 다음은 이력과 같은 결(생성 → 시작). 여섯 다 서랍에
   *    같은 응답 값이 선다 — 「작업」 카드(작업자 · 생성 · 작업 시작 · 작업 종료) · 「레시피」 카드(레시피) · 「시료」 카드 표의 건수(시료 수)
   *    (2026-10-22 eric 「시료 수 빼고... 시작을.. 작업 시작, 종료를 작업 종료」)
   * 🔴 작업자(`owner`)는 admin 에게만 있는 열이다 — 없으면 이 칸은 쓰이지 않는다. 서랍 「작업」 카드에 같은 이름표로 선다
   */
  batches: { ended: 1, created: 2, started: 3, sampleCount: 4, owner: 5, recipe: 6 },
  /**
   * 작업 ID · 상태(종전 「결과」)는 끝까지 남는다. 생성 → 시작 → 소요 시간 → 시료 수 → 종료 → 레시피(빠지는 차례 · 열 차례와 별개)
   * 🔴 시각 셋은 2026-09-30 에 들어왔다(eric). 끝난 작업의 목록이라 «언제 끝났나»(종료)가 가장 오래 남고, 생성 · 시작이
   *    먼저 빠진다 — 소요 시간은 시작 · 종료의 뺄셈이라 그 둘 사이에 둔다. 넷 다 서랍 「작업」 카드에 같은 응답 칸이 선다
   *    (시료 수는 「시료」 카드 표의 건수 · 레시피는 「레시피」 카드 · 2026-10-22).
   *    실측(서랍 열림 · 2026-09-30 하네스): 1440 → 작업 ID · 결과 · 시료 수 · 레시피 · 종료 / 1280 → 작업 ID · 결과 · 레시피 · 종료
   * ⚠ 「보고서」 열은 2026-09-30 에 뺐다(eric) — 이 표에도 없다. 되살리면 차례도 다시 정한다
   * 🔴 「작업자」(`owner` · admin 전용 · 2026-09-30)는 시료 수 다음 · 종료 앞이다 — 서랍 「작업」 카드에 같은 이름표로 선다.
   *   admin 이 아니면 열이 없어 이 칸은 쓰이지 않는다
   */
  history: { created: 1, started: 2, duration: 3, sampleCount: 4, owner: 5, ended: 6, recipe: 7 },
  /**
   * 시료 번호 · 상태는 끝까지 남는다. 등록 시각 → 타입(서랍 머리 부제가 둘 다 적는다)
   * 🔴 2026-09-30 목록 열이 넷(시료 번호 · 상태 · 타입 · 등록 시각)으로 줄었다(eric). 환자 번호 · 단계 · 현재 모듈은
   *    목록에 없고 서랍 「시료」 카드에 선다 — 여기 둘 까닭이 없어 뺐다
   * 🔴 「작업」(`batch`)은 2026-10-29 에 타입 다음으로 돌아왔다(eric). 짧은 id(8자) 좁은 열이라 타입 다음에 빠진다 —
   *    서랍 「시료」 카드가 같은 작업을 적는다
   */
  samples: { createdAt: 1, type: 2, batch: 3 },
  /**
   * 레시피 · 상태 · 메뉴는 끝까지 남는다. 등록 → 단계 → 작업자(서랍 「요약」 카드)
   * 🔴 「작업자」(`owner`)는 admin 에게만 있는 열이다(2026-09-30 eric) — 서랍 「요약」 카드에 같은 이름표로 선다
   * ⚠ 「버전」 열은 2026-09-30 에 목록에서 뺐다(eric) — 이 표에도 없다. 값은 서랍 부제 줄이 말한다
   */
  recipes: { created: 1, steps: 2, owner: 3 },
  /** 계정 ID · 상태는 끝까지 남는다. 마지막 활동 → 역할 → 이름(서랍 부제). 「세션」 열은 2026-09-30 에 뺐다(eric) */
  members: { lastSeen: 1, role: 2, name: 3 },
  /**
   * 이름 · 통신 · 상태 · 마지막 응답은 끝까지 남는다. 숨는 것은 모듈 ID 하나(서랍 제목 아래 ID).
   * 🔴 마지막 응답이 «숨지 않는 열»인 까닭 — 서랍에 그 줄이 없다(eric 2026-10-22 「마지막 응답도 빼고」). 숨기면 값이 갈 곳이 없다.
   * 열 순서는 이름 | 통신 | 상태 | 모듈 ID | 마지막 응답이다(eric 2026-10-30) · 「제어 소유」 · 「알람」 · 「안전」 열은 없다(eric 2026-10-22)
   */
  modules: { moduleId: 1 },
} as const satisfies Record<string, Record<string, number>>

export type ListWithPriority = keyof typeof LIST_COLUMN_PRIORITY

/**
 * 작업 · 이력 목록의 「작업 ID」를 앞 8자로 **줄이는** 차례(eric 2026-09-28 「줄여」). 🔴 **0 — 어떤 열을 빼기보다 먼저다.**
 * 줄이면 약 230px 가 나와 그만큼 열이 더 살아남는다(1255×742 서랍 열림 실측). 빼기 차례(위 표 · 1부터)와 같은 숫자 축이다.
 */
export const COMPACT_BATCH_ID = 0

/** 열 정의에 붙일 `priority` — 표에 없는 열이면 `undefined`(늘 보인다) */
export function columnPriority(list: ListWithPriority, key: string): number | undefined {
  const table: Record<string, number> = LIST_COLUMN_PRIORITY[list]
  return table[key]
}

/** 열 정의 묶음에 이 표의 차례를 얹는다. 표에 없는 열은 그대로(늘 보인다) */
export function withPriority<C extends { key: string }>(
  list: ListWithPriority,
  columns: readonly C[],
): (C & { priority: number | undefined })[] {
  return columns.map((column) => ({ ...column, priority: columnPriority(list, column.key) }))
}
